var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { _T } from '../../../include/appI18n';
import { api } from '../../../include/network/network';
import { formatMoney } from '../../../include/utils';
const requestCache = new Map();
function text(key, fallback) {
    return _T[key] || fallback;
}
function camelizeKeys(value) {
    if (Array.isArray(value))
        return value.map(camelizeKeys);
    if (!value || typeof value !== 'object')
        return value;
    return Object.keys(value).reduce((result, key) => {
        const camelKey = key.length ? key.charAt(0).toLowerCase() + key.slice(1) : key;
        result[camelKey] = camelizeKeys(value[key]);
        return result;
    }, {});
}
function loadConditions(priceInternal, checkIn) {
    return __awaiter(this, void 0, void 0, function* () {
        const cacheKey = `${priceInternal}|${checkIn}`;
        let request = requestCache.get(cacheKey);
        if (!request) {
            request = api('post', '/Hotel/GetHotelRoomCancellationPolicy', { priceInternal, checkIn })
                .then(result => {
                const conditions = result.response ? camelizeKeys(result.response) : null;
                if (!conditions || conditions.conditionsAvailable === false) {
                    requestCache.delete(cacheKey);
                    return null;
                }
                return conditions;
            })
                .catch(() => {
                requestCache.delete(cacheKey);
                return null;
            });
            requestCache.set(cacheKey, request);
        }
        return request;
    });
}
function calculatePenalty(price, details) {
    var _a;
    if (!details)
        return 0;
    const penaltyType = String(details.penaltyType);
    if (penaltyType === '0' || penaltyType === 'FlatFee')
        return details.flatFee || 0;
    const nights = Math.max(1, details.nights || 1);
    const nightPrice = ((_a = price.dayRates) === null || _a === void 0 ? void 0 : _a.length)
        ? price.dayRates.slice(0, nights).reduce((sum, value) => sum + value, 0)
        : price.price / Math.max(1, price.nights || 1) * nights;
    if (penaltyType === '1' || penaltyType === 'FlatNights')
        return nightPrice;
    if (penaltyType === '2' || penaltyType === 'PercentageNights')
        return nightPrice * (details.percentage || 0) / 100;
    return price.price * (details.percentage || 0) / 100;
}
function isPenaltyPolicy(policyType) {
    return String(policyType) === '2' || String(policyType) === 'Penalty';
}
function hasPenaltyValue(details) {
    if (!details)
        return false;
    const penaltyType = String(details.penaltyType);
    if (penaltyType === '0' || penaltyType === 'FlatFee')
        return details.flatFee != null;
    if (penaltyType === '1' || penaltyType === 'FlatNights')
        return details.nights != null;
    return details.percentage != null;
}
export function mapExternalHotelPriceToConditions(price) {
    var _a, _b;
    const penalties = (price.cancellationPolicies || [])
        .filter(policy => isPenaltyPolicy(policy.policyType) && hasPenaltyValue(policy.penaltyDetails))
        .map(policy => {
        var _a;
        const fromLocal = policy.startingDateLocal || policy.startingDate;
        const fromUtc = policy.startingDateUtc;
        return {
            fromLocal,
            fromUtc,
            timeZoneId: policy.timeZoneId || price.timeZoneId,
            currencyCode: ((_a = policy.penaltyDetails) === null || _a === void 0 ? void 0 : _a.flatFeeCurrency) || price.currency,
            description: policy.description,
            effectiveImmediately: !fromLocal && !fromUtc,
            sum: calculatePenalty(price, policy.penaltyDetails),
        };
    });
    return {
        conditionsAvailable: true,
        cancellationDenied: price.isRefundable === false,
        penaltyCalculationRules: penalties,
        isRefundable: price.isRefundable,
        cancellationDeadlineLocal: price.cancellationDeadlineLocal,
        cancellationDeadlineUtc: price.cancellationDeadlineUtc,
        timeZoneId: price.timeZoneId || ((_b = (_a = price.cancellationPolicies) === null || _a === void 0 ? void 0 : _a.find(policy => !!policy.timeZoneId)) === null || _b === void 0 ? void 0 : _b.timeZoneId),
        checkInTime: price.checkInBase || price.checkInTime || price.checkInFrom,
        checkOutTime: price.checkOutBase || price.checkOutTime || price.checkOutFrom,
        placementsDescription: price.placementsDescription,
        placements: price.placements,
        taxes: price.taxes,
        roomName: price.originalRoomName || price.roomTypeName,
        roomCategoryName: price.roomCategoryName,
        mealName: price.mealTypeName,
        price: price.price,
        currency: price.currency,
    };
}
function formatDateTime(value) {
    if (!value)
        return '';
    const parsed = moment.parseZone(value);
    return parsed.isValid() ? parsed.format('L LT') : value;
}
function formatTime(value) {
    if (!value)
        return '';
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : value;
}
function formatTimeZone(timeZoneId, date) {
    const parsed = date ? moment.parseZone(date) : null;
    const offset = (parsed === null || parsed === void 0 ? void 0 : parsed.isValid()) ? `UTC${parsed.format('Z')}` : '';
    return [timeZoneId, offset].filter((value, index, values) => !!value && values.indexOf(value) === index).join(', ');
}
function countPlacements(placements, selector) {
    return (placements || []).filter(selector).reduce((sum, placement) => sum + (placement.count || 0), 0);
}
export function HotelStayConditionsContent({ conditions }) {
    var _a, _b, _c, _d;
    const deadline = conditions.cancellationDeadlineLocal || conditions.cancellationDeadlineUtc;
    const mainBeds = countPlacements(conditions.placements, placement => placement.isMain);
    const extraBeds = countPlacements(conditions.placements, placement => placement.isExtra);
    const withoutBed = countPlacements(conditions.placements, placement => placement.withoutBed);
    const stayTimeZone = formatTimeZone(conditions.timeZoneId);
    const cancellationTimeZone = formatTimeZone(conditions.timeZoneId, deadline || ((_b = (_a = conditions.penaltyCalculationRules) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.fromLocal));
    const room = [conditions.roomName, conditions.roomCategoryName]
        .filter((value, index, values) => !!value && values.indexOf(value) === index)
        .join(' / ');
    return React.createElement("section", { className: "hotel-stay-conditions" },
        React.createElement("dl", { className: "hotel-stay-conditions__details" },
            room ? React.createElement("div", null,
                React.createElement("dt", null, _T.COMMON_ROOM_CATEGORY || 'Номер'),
                React.createElement("dd", null, room)) : null,
            conditions.mealName ? React.createElement("div", null,
                React.createElement("dt", null, _T.COMMON_MEAL || 'Питание'),
                React.createElement("dd", null, conditions.mealName)) : null,
            conditions.price != null ? React.createElement("div", null,
                React.createElement("dt", null, _T.COMMON_PRICE || 'Цена'),
                React.createElement("dd", null,
                    formatMoney(conditions.price, { localeAware: true, currency: conditions.currency }),
                    " ",
                    conditions.currency)) : null,
            conditions.placementsDescription || mainBeds || extraBeds || withoutBed ? React.createElement("div", null,
                React.createElement("dt", null, text('HOTEL_PLACEMENTS', 'Размещение')),
                React.createElement("dd", null,
                    conditions.placementsDescription ? React.createElement("div", null, conditions.placementsDescription) : null,
                    React.createElement("div", { className: "hotel-stay-conditions__placement-counts" },
                        mainBeds ? React.createElement("span", null,
                            text('HOTEL_MAIN_BEDS', 'Основные места'),
                            ": ",
                            mainBeds) : null,
                        extraBeds ? React.createElement("span", null,
                            text('HOTEL_EXTRA_BEDS', 'Дополнительные места'),
                            ": ",
                            extraBeds) : null,
                        withoutBed ? React.createElement("span", null,
                            text('HOTEL_WITHOUT_BED', 'Без отдельного места'),
                            ": ",
                            withoutBed) : null))) : null,
            conditions.checkInTime ? React.createElement("div", null,
                React.createElement("dt", null, _T.COMMON_CHECK_IN_TIME || 'Заезд'),
                React.createElement("dd", null,
                    formatTime(conditions.checkInTime),
                    stayTimeZone ? ` (${stayTimeZone})` : '')) : null,
            conditions.checkOutTime ? React.createElement("div", null,
                React.createElement("dt", null, _T.COMMON_CHECK_OUT_TIME || 'Выезд'),
                React.createElement("dd", null,
                    formatTime(conditions.checkOutTime),
                    stayTimeZone ? ` (${stayTimeZone})` : '')) : null,
            React.createElement("div", null,
                React.createElement("dt", null, _T.CANCELLATION_CONDITIONS || 'Условия отмены'),
                React.createElement("dd", null,
                    conditions.cancellationDenied ? React.createElement("div", null, _T.HOTEL_NON_CANCEL_TITLE || _T.CANCELLATION_PENALTY) : null,
                    !conditions.cancellationDenied && conditions.isRefundable && deadline ? React.createElement("div", null,
                        text('HOTEL_FREE_CANCELLATION_UNTIL', 'Бесплатная отмена до'),
                        " ",
                        formatDateTime(deadline),
                        cancellationTimeZone ? ` (${cancellationTimeZone})` : '') : null,
                    !conditions.cancellationDenied && conditions.isRefundable && !deadline && !((_c = conditions.penaltyCalculationRules) === null || _c === void 0 ? void 0 : _c.length) ? React.createElement("div", null, _T.CANCELLATION_FREE) : null,
                    (conditions.penaltyCalculationRules || []).map((rule, index) => {
                        var _a;
                        const from = rule.fromLocal || rule.fromUtc || rule.from;
                        const ruleTimeZone = formatTimeZone(rule.timeZoneId || conditions.timeZoneId, rule.fromLocal || rule.fromUtc);
                        const currency = rule.currencyCode || ((_a = rule.currency) === null || _a === void 0 ? void 0 : _a.name) || conditions.currency || '';
                        const startsAt = rule.effectiveImmediately
                            ? text('HOTEL_CANCELLATION_IMMEDIATELY', 'С момента бронирования')
                            : `${_T.CANCELLATION_FROM} ${formatDateTime(from)}`;
                        return React.createElement("div", { className: "hotel-stay-conditions__penalty", key: `${from || 'immediate'}-${index}` },
                            React.createElement("span", null,
                                _T.CANCELLATION_PENALTY,
                                ": ",
                                formatMoney(rule.sum || 0, { localeAware: true, currency }),
                                " ",
                                currency),
                            React.createElement("span", null,
                                startsAt,
                                ruleTimeZone ? ` (${ruleTimeZone})` : ''),
                            rule.description ? React.createElement("span", null, rule.description) : null);
                    }))),
            ((_d = conditions.taxes) === null || _d === void 0 ? void 0 : _d.length) ? React.createElement("div", null,
                React.createElement("dt", null, _T.HOTEL_DESCRIPTION_TAXES_HEADER || 'Дополнительные сборы в отеле'),
                React.createElement("dd", null, conditions.taxes.map((tax, index) => React.createElement("div", { key: `${tax.name}-${index}` },
                    tax.name,
                    ": ",
                    formatMoney(tax.price, { localeAware: true, currency: tax.currency }),
                    " ",
                    tax.currency,
                    " (",
                    tax.included
                        ? text('HOTEL_INCLUDED_IN_PRICE', 'Включено в стоимость')
                        : text('HOTEL_TAX_PAID_AT_HOTEL', 'Оплачивается в отеле'),
                    ")",
                    tax.description ? React.createElement("span", { className: "hotel-stay-conditions__tax-description" }, tax.description) : null)))) : null));
}
export function HotelOfferConditions(props) {
    const mappedExternalPrice = useMemo(() => props.externalPrice ? mapExternalHotelPriceToConditions(props.externalPrice) : null, [props.externalPrice]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [conditions, setConditions] = useState(mappedExternalPrice);
    useEffect(() => setConditions(mappedExternalPrice), [mappedExternalPrice]);
    const show = () => __awaiter(this, void 0, void 0, function* () {
        setOpen(true);
        if (conditions || !props.priceInternal || !props.checkIn)
            return;
        setLoading(true);
        setConditions(yield loadConditions(props.priceInternal, props.checkIn));
        setLoading(false);
    });
    return React.createElement(React.Fragment, null,
        React.createElement("button", { type: "button", className: props.className || 'hotel-offer-conditions__trigger', onClick: show }, props.label || text('HOTEL_STAY_CONDITIONS', 'Условия проживания')),
        React.createElement(Dialog, { open: open, onClose: () => setOpen(false), maxWidth: "sm", fullWidth: true },
            React.createElement(DialogTitle, null, text('HOTEL_STAY_CONDITIONS', 'Условия проживания')),
            React.createElement(DialogContent, { dividers: true },
                loading ? React.createElement("div", { className: "hotel-stay-conditions__loading" }, "\u2026") : null,
                !loading && conditions ? React.createElement(HotelStayConditionsContent, { conditions: conditions }) : null,
                !loading && !conditions ? React.createElement("div", null, text('HOTEL_CONDITIONS_UNAVAILABLE', 'Не удалось загрузить актуальные условия проживания')) : null),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: () => setOpen(false), color: "primary" }, _T.COMMON_CLOSE || 'Закрыть'))));
}
